var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, makeObservable } from 'mobx';
import { alias, autodetect, deserialize, object, primitive, withDefault } from '../../../utils/serializr/serializr';
import { TraitCondition } from '../condition/TraitCondition';
import { TraitPriority } from './modules/TraitPriority';
import { TraitValue } from './modules/TraitValue';
import { traitRegistry } from '../../../registry/Registry';
import { memoize } from '../../../utils/mobx/decorators/memoize';
import { TraitTypeNumeric } from '../../../registry/trait/traitTypes/TraitTypeNumeric';
import { Actor } from '../../actor/Actor';
import { ensureValidTraitRegistryKey } from '../../../utils/serializr/schema/specific/trait/ensureValidTraitRegistryKey';
import { editable } from '../../../utils/mobx/decorators/editable';
import { round } from '../../../utils/helper/lodash_replacements';
import { runtime } from '../../runtime/Runtime';
// import { fixValue } from '../../../utils/serializr/schema/middle/fixValue';
export class Trait extends Actor {
    type = 'trait';
    key;
    createdBy;
    value_stored; // 19.10.2020: ref, because proxy arrays (when plain arrays) are difficult to handle
    priority = 0;
    condition;
    isTraitCollapsed = true;
    traitGroup = ''; // unify by this group name
    isGenerated = false;
    hasValueFromRegistry = false;
    constructor(init) {
        super(init);
        makeObservable(this);
    }
    get traitEdit() {
        return new runtime.solidPackages.Edit.classes.TraitEdit(this);
    }
    get traitPriority() {
        return new TraitPriority(this);
    }
    get traitValue() {
        return new TraitValue(this);
    }
    get id() {
        return super.id;
    }
    set id(id) {
        super.id = id;
    }
    get readableIdentifier() {
        return `trait${this.id}`;
    }
    get aspect() {
        return this.parent;
    }
    get actor() {
        return this.aspect.actor;
    }
    get reg() {
        return traitRegistry.registry_object[this.key];
    }
    get isMultiple() {
        return !!this.reg && !!this.reg.multiple;
    }
    get toAttached() {
        return this.parent.aspectAttached.toAttached(this);
    }
    get value() {
        return this.traitValue.value;
    }
    set value(v) {
        let newValue = v;
        if (this.reg instanceof TraitTypeNumeric && typeof this.reg.roundDecimals !== 'undefined') {
            newValue = round(newValue, this.reg.roundDecimals);
        }
        this.value_stored = newValue;
    }
    get nthLocalPosition() {
        return (1 +
            this.aspect.traits
                .filter(trait => trait.key === this.key && (trait === this || !trait.traitGroup || trait.traitGroup !== this.traitGroup))
                .indexOf(this));
    }
    static fromSerialized(json = {}) {
        return deserialize(Trait, json);
    }
    static get serializeInfo() {
        return {
            extends: Actor.serializeInfo,
            props: {
                key: ensureValidTraitRegistryKey('traitRegistry', primitive),
                value_stored: /*fixValue(*/ alias('value', autodetect()) /*)*/,
                condition: object(TraitCondition),
                priority: withDefault(primitive, 0),
                traitGroup: withDefault(primitive, ''),
                hasValueFromRegistry: withDefault(primitive, false),
                isTraitCollapsed: withDefault(primitive, true),
            },
        };
    }
}
__decorate([
    editable()
], Trait.prototype, "key", void 0);
__decorate([
    editable('ref')
], Trait.prototype, "value_stored", void 0);
__decorate([
    editable()
], Trait.prototype, "priority", void 0);
__decorate([
    editable('ref')
], Trait.prototype, "condition", void 0);
__decorate([
    editable()
], Trait.prototype, "isTraitCollapsed", void 0);
__decorate([
    editable()
], Trait.prototype, "traitGroup", void 0);
__decorate([
    editable()
], Trait.prototype, "isGenerated", void 0);
__decorate([
    editable()
], Trait.prototype, "hasValueFromRegistry", void 0);
__decorate([
    memoize
], Trait.prototype, "traitEdit", null);
__decorate([
    memoize
], Trait.prototype, "traitPriority", null);
__decorate([
    memoize
], Trait.prototype, "traitValue", null);
__decorate([
    memoize
], Trait.prototype, "reg", null);
__decorate([
    computed({ requiresReaction: true })
], Trait.prototype, "toAttached", null);
__decorate([
    computed
], Trait.prototype, "value", null);
__decorate([
    memoize
], Trait, "serializeInfo", null);
